import React, { useState } from "react";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";

import {
  appApi,
  useGetEmployeeImprestLedgerQuery,
} from "../../../store/appApi";
import {
  formatCurrency,
  formatDateLabel,
  formatRoleLabel,
  toDateInputValue,
} from "./imprestConfig";
import {
  bookImprestExpenseApi,
  deleteImprestEntryApi,
  updateImprestEntryApi,
} from "../../../services/imprest";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 text-transform-capitalize";

const typeChip = (type) =>
  type === "Allotment"
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    : "bg-rose-50 text-rose-700 ring-1 ring-rose-200";

const balanceClass = (value) => {
  if (value > 0) return "text-emerald-600";
  if (value < 0) return "text-rose-600";
  return "text-gray-500";
};

// Shared ledger view for both the admin Detail page (canManage=true, any
// employee) and the employee's own self-service page (canManage=
// "own-expenses-only", always their own id).
const EmployeeLedger = ({ employeeId, canManage }) => {
  const dispatch = useDispatch();
  const canEditEntries =
    canManage === true || canManage === "own-expenses-only";

  const { data, isLoading, isFetching } = useGetEmployeeImprestLedgerQuery(
    employeeId,
    { skip: !employeeId },
  );

  const [expenseDate, setExpenseDate] = useState(new Date());
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseRemarks, setExpenseRemarks] = useState("");
  const [bookingExpense, setBookingExpense] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);

  const employee = data?.employee || null;
  const ledger = Array.isArray(data?.ledger) ? data.ledger : [];
  const summary = data?.summary || {};
  const loading = isLoading || isFetching;

  // Enter inside any field (e.g. the date picker) shouldn't silently submit
  // the form before Amount has been filled in — only the Book Expense
  // button should trigger a save.
  const handleFormKeyDown = (e) => {
    if (e.key === "Enter" && e.target.type !== "submit") {
      e.preventDefault();
    }
  };

  const handleBookExpense = async (e) => {
    e.preventDefault();
    const numericAmount = Number(expenseAmount);
    if (!numericAmount || numericAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: "Please enter an amount greater than zero.",
      });
      return;
    }

    try {
      setBookingExpense(true);
      await bookImprestExpenseApi({
        // Only pass an explicit employeeId when the admin is booking on
        // someone else's behalf — the backend defaults to the caller's own
        // id otherwise, which is what the self-service page relies on.
        ...(canManage === true ? { employeeId } : {}),
        date: expenseDate.toISOString(),
        amount: numericAmount,
        remarks: expenseRemarks,
      });
      dispatch(appApi.util.invalidateTags(["Imprest"]));
      Swal.fire({
        icon: "success",
        title: "Booked",
        timer: 1400,
        showConfirmButton: false,
      });
      setExpenseAmount("");
      setExpenseRemarks("");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to book expense",
        text:
          error?.response?.data?.message ||
          "Something went wrong while booking this expense.",
      });
    } finally {
      setBookingExpense(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry._id);
    setEditDraft({
      date: toDateInputValue(entry.date),
      amount: entry.amount,
      remarks: entry.remarks || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const updateDraft = (key, value) =>
    setEditDraft((prev) => ({ ...prev, [key]: value }));

  const saveEdit = async (entryId) => {
    const numericAmount = Number(editDraft.amount);
    if (!numericAmount || numericAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: "Please enter an amount greater than zero.",
      });
      return;
    }

    try {
      setRowBusy(entryId);
      await updateImprestEntryApi(entryId, {
        date: editDraft.date,
        amount: numericAmount,
        remarks: editDraft.remarks,
      });
      dispatch(appApi.util.invalidateTags(["Imprest"]));
      Swal.fire({
        icon: "success",
        title: "Updated",
        timer: 1400,
        showConfirmButton: false,
      });
      cancelEdit();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to update",
        text:
          error?.response?.data?.message ||
          "Something went wrong while updating this entry.",
      });
    } finally {
      setRowBusy(null);
    }
  };

  const handleDelete = async (entry) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete this expense?",
      text: `${formatCurrency(entry.amount)} on ${formatDateLabel(entry.date)} will be permanently removed.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;

    try {
      setRowBusy(entry._id);
      await deleteImprestEntryApi(entry._id);
      dispatch(appApi.util.invalidateTags(["Imprest"]));
      Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to delete",
        text:
          error?.response?.data?.message ||
          "Something went wrong while deleting this entry.",
      });
    } finally {
      setRowBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <section className={`${panelClass} p-6`}>
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">
            {loading ? "Loading…" : employee?.name || "Staff member"}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            {formatRoleLabel(employee?.role)}
          </p>
        </div>

        <div className="mt-5 grid max-w-xl grid-cols-3 gap-3 text-center text-xs text-gray-600">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
            <div className="text-gray-400">Total Allotted</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {formatCurrency(summary.totalAllotted)}
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
            <div className="text-gray-400">Total Expensed</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {formatCurrency(summary.totalExpensed)}
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
            <div className="text-gray-400">Balance</div>
            <div
              className={`mt-1 text-lg font-semibold ${balanceClass(Number(summary.balance) || 0)}`}
            >
              {formatCurrency(summary.balance)}
            </div>
          </div>
        </div>
      </section>

      {canManage && (
        <section className={`${panelClass} p-5 md:p-6`}>
          <h2 className="text-lg font-semibold text-gray-900">
            Book an Expense
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Records how the allotted cash was spent and reduces this imprest
            balance. Does not affect the main Daybook.
          </p>
          <form
            onSubmit={handleBookExpense}
            onKeyDown={handleFormKeyDown}
            className="mt-4 grid gap-4 md:grid-cols-4"
          >
            <div>
              <label className={labelClass}>Date</label>
              <DatePicker
                selected={expenseDate}
                onChange={(date) => date && setExpenseDate(date)}
                className={inputClass}
                autoComplete="off"
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </div>
            <div>
              <label className={labelClass}>Amount</label>
              <input
                type="number"
                min="0"
                max={Number(summary.balance) || 0}
                step="0.01"
                className={inputClass}
                autoComplete="off"
                value={expenseAmount}
                onChange={(event) => setExpenseAmount(event.target.value)}
                placeholder="0.00"
              />
              <p className="mt-1 text-xs text-gray-500">
                Available balance:{" "}
                <span className="font-semibold text-gray-900">
                  {formatCurrency(summary.balance)}
                </span>
              </p>
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Remarks</label>
              <input
                className={inputClass}
                autoComplete="off"
                value={expenseRemarks}
                onChange={(event) => setExpenseRemarks(event.target.value)}
                placeholder="What was this spent on?"
              />
            </div>
            <div className="md:col-span-4 flex justify-end">
              <button
                type="submit"
                disabled={bookingExpense}
                className="rounded-full bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {bookingExpense ? "Saving..." : "Book Expense"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className={panelClass}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Full Ledger</h2>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
            {summary.entriesCount ?? ledger.length} entries
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-5 py-2.5 text-left font-medium">Date</th>
                <th className="px-5 py-2.5 text-left font-medium">Type</th>
                <th className="px-5 py-2.5 text-right font-medium">Amount</th>
                <th className="px-5 py-2.5 text-right font-medium">
                  Running Balance
                </th>
                <th className="px-5 py-2.5 text-left font-medium">Remarks</th>
                {canEditEntries && (
                  <th className="px-5 py-2.5 text-right font-medium">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading && (
                <tr>
                  <td
                    colSpan={canEditEntries ? 6 : 5}
                    className="px-5 py-10 text-center text-gray-500"
                  >
                    Loading ledger...
                  </td>
                </tr>
              )}

              {!loading && ledger.length === 0 && (
                <tr>
                  <td
                    colSpan={canEditEntries ? 6 : 5}
                    className="px-5 py-10 text-center text-gray-500"
                  >
                    No entries recorded yet.
                  </td>
                </tr>
              )}

              {!loading &&
                ledger.map((entry) => {
                  const isEditing = editingId === entry._id;
                  const busy = rowBusy === entry._id;
                  const canEditThisRow =
                    canEditEntries && entry.type === "Expense";

                  if (isEditing) {
                    return (
                      <tr key={entry._id} className="bg-amber-50/40">
                        <td className="px-5 py-2.5">
                          <input
                            type="date"
                            className={inputClass}
                            value={editDraft.date}
                            onChange={(event) =>
                              updateDraft("date", event.target.value)
                            }
                          />
                        </td>
                        <td className="px-5 py-2.5">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${typeChip(entry.type)}`}
                          >
                            {entry.type}
                          </span>
                        </td>
                        <td className="px-5 py-2.5">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className={`${inputClass} text-right`}
                            value={editDraft.amount}
                            onChange={(event) =>
                              updateDraft("amount", event.target.value)
                            }
                          />
                        </td>
                        <td className="px-5 py-2.5 text-right text-gray-400">
                          —
                        </td>
                        <td className="px-5 py-2.5">
                          <input
                            className={inputClass}
                            value={editDraft.remarks}
                            onChange={(event) =>
                              updateDraft("remarks", event.target.value)
                            }
                            placeholder="Remarks"
                          />
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={() => saveEdit(entry._id)}
                            >
                              {busy ? "Saving..." : "Save"}
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              className="rounded-lg border-2 border-amber-500 px-3 py-1.5 text-xs font-semibold text-gray-600"
                              onClick={cancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={entry._id}>
                      <td className="px-5 py-2.5 text-gray-600">
                        {formatDateLabel(entry.date)}
                      </td>
                      <td className="px-5 py-2.5">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${typeChip(entry.type)}`}
                        >
                          {entry.type}
                        </span>
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-900">
                        {formatCurrency(entry.amount)}
                      </td>
                      <td
                        className={`px-5 py-2.5 text-right font-semibold ${balanceClass(Number(entry.runningBalance) || 0)}`}
                      >
                        {formatCurrency(entry.runningBalance)}
                      </td>
                      <td className="px-5 py-2.5 text-gray-600">
                        {entry.remarks || "—"}
                      </td>
                      {canEditEntries && (
                        <td className="px-5 py-2.5 text-right">
                          {canEditThisRow ? (
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                disabled={busy}
                                className="rounded-lg border-2 border-amber-500 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={() => startEdit(entry)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-400 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={() => handleDelete(entry)}
                              >
                                {busy ? "..." : "Delete"}
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">
                              Locked
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default EmployeeLedger;
